import React, { useEffect, useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { procedureRecordingsAPI } from "../services/api";
import ProcedureBotAvailabilityField from "./ProcedureBotAvailabilityField";

export default function ProcedureRecordingEditDialog({ recording, onOpenChange, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sharedWithTeam, setSharedWithTeam] = useState(false);
  const [availableInBot, setAvailableInBot] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setTitle(recording?.title || "");
    setDescription(recording?.description || "");
    setSharedWithTeam(recording?.shared_with_team === true);
    setAvailableInBot(recording?.available_in_bot === true);
    setError("");
    setIsSaving(false);
  }, [recording]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!title.trim()) {
      setError("Informe um título para o procedimento.");
      return;
    }

    setError("");
    setIsSaving(true);
    try {
      const response = await procedureRecordingsAPI.updateMetadata(recording.id, {
        title: title.trim(),
        description: description.trim(),
        shared_with_team: sharedWithTeam,
        available_in_bot: availableInBot,
      });
      onSaved?.(response.data);
      onOpenChange(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível atualizar este procedimento.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(recording)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto rounded-xl sm:max-w-lg" onEscapeKeyDown={(event) => { if (isSaving) event.preventDefault(); }} onPointerDownOutside={(event) => { if (isSaving) event.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5 text-violet-600" /> Editar procedimento</DialogTitle>
          <DialogDescription>Corrija o título e a descrição para facilitar a busca e o envio ao cliente.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="procedure-edit-title">Título</Label>
            <Input id="procedure-edit-title" value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160} disabled={isSaving} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="procedure-edit-description">Descrição</Label>
            <Textarea id="procedure-edit-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={4000} disabled={isSaving} rows={4} />
          </div>
          <label className="flex items-start gap-3 rounded-lg border p-3 text-sm cursor-pointer hover:bg-gray-50">
            <input
              id="procedure-edit-shared-with-team"
              type="checkbox"
              checked={sharedWithTeam}
              onChange={(event) => setSharedWithTeam(event.target.checked)}
              disabled={isSaving}
              className="mt-0.5 h-4 w-4 accent-violet-600"
            />
            <span>
              <strong>Disponibilizar para a equipe</strong>
              <span className="block text-gray-500">Desmarque para restringir o acesso interno a você e aos administradores.</span>
            </span>
          </label>
          <ProcedureBotAvailabilityField checked={availableInBot} onChange={setAvailableInBot} disabled={isSaving} />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancelar</Button>
            <Button type="submit" disabled={isSaving} className="bg-violet-600 hover:bg-violet-700">
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar dados
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
